import { useEffect, useState } from 'react';

/**
 * A minimal hash router: the site map's six routes are all literal `#<name>` fragments,
 * and a fragment never
 * reaches the server, so "paste a URL, land on that screen" needs nothing beyond reading
 * `window.location.hash`. **No router library**: `react-router`'s `HashRouter` always prepends a
 * slash (`#/processing`, not `#processing`), which would silently drift from the site map's own
 * verbatim route names, and six static segments plus one `:id` param plus one `?q=` query string
 * do not need a route-matching engine.
 */
export interface HashRoute {
  /** The whole fragment, minus its leading `#` and any trailing `?query`. Empty string at the
   *  bare app root. */
  path: string;
  /** `path` split on `/`. `['processing']`, `['doc', 'pedimento', 'case-123']`, `['']` at the root. */
  segments: string[];
  /** The fragment's own `?query` half, `#documents?q=INV-9`'s `q=INV-9` — OPS-02's addition, so a
   *  pasted search URL reproduces the same search rather than landing on a blank `#documents`. */
  query: URLSearchParams;
}

/** The current route, read straight from `window.location.hash`. `useHashRoute` keeps it in state. */
export function readHash(): HashRoute {
  const raw = window.location.hash.replace(/^#/, '');
  const queryIndex = raw.indexOf('?');
  const path = queryIndex === -1 ? raw : raw.slice(0, queryIndex);
  const queryString = queryIndex === -1 ? '' : raw.slice(queryIndex + 1);
  return { path, segments: path.split('/'), query: new URLSearchParams(queryString) };
}

/** Navigates to `#documents?q=<query>`, the one non-nav way onto the search screen.
 * An empty
 *  query navigates to the bare `#documents` prompt screen.
 *
 *  Resubmitting the same query is a real operator move (retyping the same number after Stop, to
 *  restart the widening scan), but assigning `location.hash` to a byte-identical fragment does not
 *  fire `hashchange` (browser spec), so `useHashRoute` would never re-trigger and the screen would
 *  stay frozen with no feedback. Dispatch the same event by hand when the fragment does not
 *  actually change. */
export function navigateToSearch(query: string): void {
  const trimmed = query.trim();
  const target = trimmed.length > 0 ? `documents?q=${encodeURIComponent(trimmed)}` : 'documents';
  const current = window.location.hash.replace(/^#/, '');
  window.location.hash = target;
  if (current === target) window.dispatchEvent(new Event('hashchange'));
}

export function useHashRoute(): HashRoute {
  const [route, setRoute] = useState<HashRoute>(readHash);

  useEffect(() => {
    const onHashChange = () => setRoute(readHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  return route;
}
